/*
  Photo reel (sections/nr-photo-reel.liquid).
  The strip is a sideways scroller. This script repeats the photos until one
  set is more than twice as wide as the screen, adds two hidden copies of
  that set and starts in the middle one, so the strip can run on, or be
  swiped or stepped either way, and quietly jump back or forward by a whole
  set when it nears an end. It moves on its own only while it is on screen
  and nobody is hovering, focusing, touching or stepping it, and never for
  people who prefer reduced motion; swiping and the arrows still work.
*/
if (!customElements.get('nr-photo-reel')) {
  customElements.define(
    'nr-photo-reel',
    class NrPhotoReel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.nr-photo-reel__viewport');
        this.track = this.querySelector('.nr-photo-reel__track');
        this.base = this.track && this.track.querySelector('.nr-photo-reel__group');
        if (!this.viewport || !this.base || !this.base.children.length) return;

        this.still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.holdUntil = 0;
        this.tick = this.tick.bind(this);

        this.querySelectorAll('[data-reel-step]').forEach((button) => {
          button.addEventListener('click', () => this.step(Number(button.dataset.reelStep)));
        });

        // Without motion there is nothing to loop: the strip just scrolls.
        if (this.still) return;

        // Stops while the pointer is over the photos or they have focus.
        this.viewport.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.hovering = true;
        });
        this.viewport.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.hovering = false;
        });
        this.viewport.addEventListener('focusin', () => (this.focused = true));
        this.viewport.addEventListener('focusout', () => (this.focused = false));

        this.viewport.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse') this.touching = true;
        });
        const release = () => {
          this.touching = false;
          this.hold();
        };
        this.viewport.addEventListener('pointerup', release);
        this.viewport.addEventListener('pointercancel', release);
        this.viewport.addEventListener(
          'wheel',
          (event) => {
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.hold();
          },
          { passive: true }
        );

        // Once a swipe or a step has settled, move back into the middle set.
        this.viewport.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.idleTimer);
            this.idleTimer = setTimeout(() => {
              if (!this.touching) this.recentre();
            }, 200);
          },
          { passive: true }
        );

        requestAnimationFrame(() => this.build());

        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((entry) => entry.isIntersecting);
            this.run();
          },
          { rootMargin: '200px 0px', threshold: 0.01 }
        );
        this.observer.observe(this);

        this.onResize = () => {
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => this.build());
        };
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      // Fills the set and makes the two copies; again on resize if the
      // screen has outgrown them.
      build() {
        const minWidth = this.viewport.clientWidth * 2 + 80;
        if (this.built && this.base.scrollWidth >= minWidth) return this.measure();

        this.track.querySelectorAll('.nr-photo-reel__group').forEach((group) => {
          if (group !== this.base) group.remove();
        });
        const originals = Array.from(this.base.children).filter((node) => !node.hasAttribute('aria-hidden'));
        this.originals = originals.length;
        for (let guard = 0; this.base.scrollWidth < minWidth && guard < 25; guard++) {
          originals.forEach((node) => this.base.appendChild(this.decorative(node.cloneNode(true))));
        }
        this.track.appendChild(this.decorative(this.base.cloneNode(true)));
        this.track.appendChild(this.decorative(this.base.cloneNode(true)));

        this.built = true;
        this.measure();
        this.viewport.scrollLeft = this.shift;
        this.pos = this.viewport.scrollLeft;
        this.run();
      }

      // Copies are for the loop only, so screen readers skip them.
      decorative(node) {
        node.setAttribute('aria-hidden', 'true');
        node.querySelectorAll('img').forEach((img) => img.setAttribute('alt', ''));
        node.removeAttribute('data-shopify-editor-block');
        return node;
      }

      // One set's width, the gap between photos included, and the speed
      // that runs the photos past once in the "Time for one full loop"
      // setting (the set may hold them more than once).
      measure() {
        this.shift = this.base.nextElementSibling.offsetLeft - this.base.offsetLeft;
        const seconds = parseFloat(getComputedStyle(this).getPropertyValue('--nr-reel-speed')) || 47;
        this.speed = (this.shift * this.originals) / this.base.children.length / seconds;
      }

      run() {
        if (this.visible && this.built && !this.frame) {
          this.last = 0;
          this.frame = requestAnimationFrame(this.tick);
        } else if (!this.visible && this.frame) {
          cancelAnimationFrame(this.frame);
          this.frame = null;
        }
      }

      tick(now) {
        this.frame = requestAnimationFrame(this.tick);
        const elapsed = this.last ? Math.min(now - this.last, 100) : 0;
        this.last = now;
        if (this.hovering || this.focused || this.touching || now < this.holdUntil) return;

        // Carry on from wherever a shopper left it.
        if (Math.abs(this.viewport.scrollLeft - this.pos) > 2) this.pos = this.viewport.scrollLeft;
        this.pos += (this.speed * elapsed) / 1000;
        if (this.pos >= this.shift * 2) this.pos -= this.shift;
        if (this.pos < this.shift * 0.5) this.pos += this.shift;
        this.viewport.scrollLeft = this.pos;
      }

      // Pauses the motion for a while after a shopper moves the reel.
      hold(ms = 3000) {
        this.holdUntil = performance.now() + ms;
      }

      recentre() {
        if (!this.built) return;
        const left = this.viewport.scrollLeft;
        if (left < this.shift * 0.5) this.viewport.scrollLeft = left + this.shift;
        else if (left >= this.shift * 2) this.viewport.scrollLeft = left - this.shift;
        this.pos = this.viewport.scrollLeft;
      }

      // Arrows: about a screen's worth of photos, stopping on a photo's edge.
      step(direction) {
        this.hold(4000);
        this.recentre();
        const items = this.base.children;
        const pitch = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : items[0].offsetWidth;
        const count = Math.max(1, Math.floor((this.viewport.clientWidth * 0.8) / pitch));
        const target = Math.round((this.viewport.scrollLeft + direction * count * pitch) / pitch) * pitch;
        this.viewport.scrollTo({ left: target, behavior: this.still ? 'auto' : 'smooth' });
      }
    }
  );
}
